/**
 * MotivationModule Component
 * Displays daily objective and employee of the month
 * Admin can edit, others view only
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
    Target, Award, Edit2, Save, X, Upload, User,
    Sparkles, Trophy, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const MotivationModule = ({ className = "" }) => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    
    const [settings, setSettings] = useState({
        daily_objective: { amount: 0, message: "" },
        employee_of_month: { name: "", position: "", photo_url: "", message: "" }
    });
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editType, setEditType] = useState(null); // 'objective' or 'employee'
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch settings on mount
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
            }
        } catch (error) {
            console.error('Error fetching motivation settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const openEditDialog = (type) => {
        setEditType(type);
        if (type === 'objective') {
            setEditData({ ...settings.daily_objective });
        } else {
            setEditData({ ...settings.employee_of_month });
        }
        setEditDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const updatePayload = editType === 'objective' 
                ? { daily_objective: editData }
                : { employee_of_month: editData };

            const response = await fetch(`${API_BASE}/api/motivation/settings`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatePayload)
            });

            if (response.ok) {
                const result = await response.json();
                setSettings(result.settings);
                setEditDialogOpen(false);
                toast.success(editType === 'objective' ? 'Objectif mis à jour !' : 'Employé du mois mis à jour !');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('La photo ne doit pas dépasser 2 Mo');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE}/api/motivation/upload-photo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                setEditData({ ...editData, photo_url: result.photo_url });
                toast.success('Photo mise à jour !');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors du téléchargement');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className={cn("animate-pulse", className)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="h-40 bg-gray-200 rounded-xl"></div>
                    <div className="h-40 bg-gray-200 rounded-xl"></div>
                </div>
            </div>
        );
    }

    const { daily_objective, employee_of_month } = settings;
    const hasObjective = daily_objective?.amount > 0;
    const hasEmployee = employee_of_month?.name;

    return (
        <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4", className)} data-testid="motivation-module">
            {/* Objectif du jour */}
            <Card className={cn(
                "relative overflow-hidden border-0 shadow-lg",
                "bg-gradient-to-br from-amber-500 via-orange-500 to-red-500"
            )}>
                {/* Background decoration - smaller on mobile */}
                <div className="absolute inset-0 opacity-10">
                    <Target className="absolute -right-4 -top-4 w-24 md:w-40 h-24 md:h-40 text-white" />
                    <Sparkles className="absolute left-2 bottom-2 w-8 md:w-12 h-8 md:h-12 text-white" />
                </div>
                
                <CardContent className="relative p-4 md:p-6 text-white">
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                        <div className="flex items-center gap-2">
                            <Target className="w-5 h-5 md:w-6 md:h-6" />
                            <h3 className="font-bold text-base md:text-lg">Objectif du jour</h3>
                        </div>
                        {isAdmin && (
                            <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-white/80 hover:text-white hover:bg-white/20 h-10 w-10 touch-target"
                                onClick={() => openEditDialog('objective')}
                                data-testid="edit-objective-btn"
                            >
                                <Edit2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                    
                    {hasObjective ? (
                        <>
                            <div className="text-center mb-2 md:mb-3">
                                <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mobile-metric">
                                    {formatCurrency(daily_objective.amount)}
                                </p>
                                <p className="text-white/80 text-xs md:text-sm mt-1">FCFA</p>
                            </div>
                            {daily_objective.message && (
                                <p className="text-center text-white/90 text-xs md:text-sm italic bg-white/10 rounded-lg p-2 line-clamp-2">
                                    "{daily_objective.message}"
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-3 md:py-4">
                            <p className="text-white/70 text-sm">Aucun objectif défini</p>
                            {isAdmin && (
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="mt-2 border-white/50 text-white hover:bg-white/20 h-10 touch-target"
                                    onClick={() => openEditDialog('objective')}
                                >
                                    Définir l'objectif
                                </Button>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Employé du mois */}
            <Card className={cn(
                "relative overflow-hidden border-0 shadow-lg",
                "bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500"
            )}>
                {/* Background decoration - smaller on mobile */}
                <div className="absolute inset-0 opacity-10">
                    <Trophy className="absolute -right-4 -top-4 w-24 md:w-40 h-24 md:h-40 text-white" />
                    <Star className="absolute left-2 bottom-2 w-8 md:w-12 h-8 md:h-12 text-white" />
                </div>
                
                <CardContent className="relative p-4 md:p-6 text-white">
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                        <div className="flex items-center gap-2">
                            <Award className="w-5 h-5 md:w-6 md:h-6" />
                            <h3 className="font-bold text-base md:text-lg">Employé du mois</h3>
                        </div>
                        {isAdmin && (
                            <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-white/80 hover:text-white hover:bg-white/20 h-10 w-10 touch-target"
                                onClick={() => openEditDialog('employee')}
                                data-testid="edit-employee-btn"
                            >
                                <Edit2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                    
                    {hasEmployee ? (
                        <div className="flex items-center gap-3 md:gap-4">
                            {/* Photo - responsive sizing */}
                            <div className="relative flex-shrink-0">
                                <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full overflow-hidden ring-3 md:ring-4 ring-yellow-400 ring-offset-2 ring-offset-purple-600 bg-white/20">
                                    {employee_of_month.photo_url ? (
                                        <img 
                                            src={employee_of_month.photo_url} 
                                            alt={employee_of_month.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <User className="w-8 h-8 md:w-10 md:h-10 text-white/60" />
                                        </div>
                                    )}
                                </div>
                                <div className="absolute -top-0.5 -right-0.5 bg-yellow-400 rounded-full p-0.5 md:p-1">
                                    <Star className="w-3 h-3 md:w-4 md:h-4 text-yellow-800 fill-yellow-800" />
                                </div>
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <p className="text-lg md:text-xl lg:text-2xl font-bold truncate">
                                    {employee_of_month.name}
                                </p>
                                <p className="text-white/80 text-xs md:text-sm">
                                    {employee_of_month.position}
                                </p>
                                {employee_of_month.message && (
                                    <p className="text-white/90 text-xs mt-1.5 md:mt-2 italic bg-white/10 rounded p-1 md:p-1.5 line-clamp-2">
                                        "{employee_of_month.message}"
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-3 md:py-4">
                            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-2">
                                <User className="w-6 h-6 md:w-8 md:h-8 text-white/60" />
                            </div>
                            <p className="text-white/70 text-sm">Aucun employé sélectionné</p>
                            {isAdmin && (
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="mt-2 border-white/50 text-white hover:bg-white/20"
                                    onClick={() => openEditDialog('employee')}
                                >
                                    Choisir l'employé
                                </Button>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {editType === 'objective' ? (
                                <>
                                    <Target className="w-5 h-5 text-orange-500" />
                                    Modifier l'objectif du jour
                                </>
                            ) : (
                                <>
                                    <Award className="w-5 h-5 text-purple-500" />
                                    Modifier l'employé du mois
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    {editType === 'objective' ? (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Montant de l'objectif (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={editData.amount || ''}
                                    onChange={(e) => setEditData({ ...editData, amount: parseInt(e.target.value) || 0 })}
                                    placeholder="Ex: 500000"
                                    className="text-lg font-semibold"
                                    data-testid="objective-amount-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Message de motivation (optionnel)</Label>
                                <Textarea
                                    value={editData.message || ''}
                                    onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                                    placeholder="Ex: Ensemble, dépassons nos limites !"
                                    rows={2}
                                    data-testid="objective-message-input"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Photo upload */}
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 ring-2 ring-purple-200 flex-shrink-0">
                                    {editData.photo_url ? (
                                        <img 
                                            src={editData.photo_url} 
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <User className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handlePhotoUpload}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        data-testid="upload-photo-btn"
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        Changer la photo
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Max 2 Mo, JPG ou PNG
                                    </p>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Nom de l'employé</Label>
                                <Input
                                    value={editData.name || ''}
                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                    placeholder="Ex: Jean Dupont"
                                    data-testid="employee-name-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Poste</Label>
                                <Input
                                    value={editData.position || ''}
                                    onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                                    placeholder="Ex: Serveur"
                                    data-testid="employee-position-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Message de félicitations (optionnel)</Label>
                                <Textarea
                                    value={editData.message || ''}
                                    onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                                    placeholder="Ex: Bravo pour ton excellent travail !"
                                    rows={2}
                                    data-testid="employee-message-input"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => setEditDialogOpen(false)}
                            disabled={saving}
                        >
                            <X className="w-4 h-4 mr-2" />
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSave}
                            disabled={saving}
                            className={editType === 'objective' 
                                ? "bg-orange-500 hover:bg-orange-600" 
                                : "bg-purple-500 hover:bg-purple-600"
                            }
                            data-testid="save-motivation-btn"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MotivationModule;
